import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import RecommendationCard from "@/components/RecommendationCard";
import { toast } from "sonner";
import { Sparkles, Compass, MapPin, CloudSun, Clock, AlertTriangle, Wand2, RefreshCw } from "lucide-react";

const MOODS = ["happy", "sad", "stressed", "tired", "energetic", "relaxed"];
const WEATHERS = ["sunny", "rainy", "cloudy", "cold", "hot"];
const MEALS = ["breakfast", "lunch", "dinner", "snack"];
const SPICES = ["low", "medium", "high"];
const DIETS = ["any", "vegetarian", "non-vegetarian", "eggitarian", "vegan"];

function Chip({ active, onClick, children, testId, small }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${small ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm"} rounded-full border capitalize transition ${
        active
          ? "bg-foreground text-background border-foreground"
          : "border-border text-foreground hover:border-foreground/40"
      }`}
    >
      {children}
    </button>
  );
}

// Session telemetry stored in sessionStorage, used as decision-fatigue signal
const TEL_KEYS = { search: "yumo_search_count", refresh: "yumo_refresh_count" };
function readTelemetry(start) {
  return {
    refresh_count: parseInt(sessionStorage.getItem(TEL_KEYS.refresh) || "0", 10),
    search_count: parseInt(sessionStorage.getItem(TEL_KEYS.search) || "0", 10),
    session_minutes: Math.round((Date.now() - start) / 60000),
  };
}
function bumpTelemetry(key) {
  const v = parseInt(sessionStorage.getItem(key) || "0", 10) + 1;
  sessionStorage.setItem(key, String(v));
}

// Browser geolocation with timeout + soft failure
function useGeolocation() {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | granted | denied | unsupported
  const request = useCallback(() => {
    if (!navigator.geolocation) { setStatus("unsupported"); return Promise.resolve(null); }
    setStatus("requesting");
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
          setCoords(c); setStatus("granted"); resolve(c);
        },
        () => { setStatus("denied"); resolve(null); },
        { timeout: 5000, maximumAge: 5 * 60 * 1000 }
      );
    });
  }, []);
  return { coords, status, request };
}

export default function Recommend() {
  const { user } = useAuth();
  const startRef = useRef(Date.now());
  const geo = useGeolocation();

  const [autoMode, setAutoMode] = useState(true);
  const [mood, setMood] = useState("happy");
  const [weather, setWeather] = useState("sunny");
  const [mealType, setMealType] = useState("dinner");
  const [spice, setSpice] = useState(user?.profile?.spice_preference || "medium");
  const [diet, setDiet] = useState(user?.profile?.diet_type || "any");

  const [previewContext, setPreviewContext] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const doInferContext = useCallback(async (c) => {
    setPreviewing(true);
    try {
      const tel = readTelemetry(startRef.current);
      const { data } = await api.post("/context/infer", {
        lat: c?.lat, lon: c?.lon,
        client_local_hour: new Date().getHours(),
        refresh_count: tel.refresh_count,
        search_count: tel.search_count,
        session_minutes: tel.session_minutes,
      });
      setPreviewContext(data);
    } catch (e) {
      console.warn("[context/infer] preview failed", e?.message);
    } finally {
      setPreviewing(false);
    }
  }, []);

  // Try silent geolocation on mount (only if auto mode + browser supports it).
  // `geo.request` and `doInferContext` are stable callbacks — included for hook-deps correctness.
  useEffect(() => {
    if (autoMode && geo.status === "idle") {
      geo.request().then((c) => doInferContext(c || null));
    }
  }, [autoMode, geo, doInferContext]);

  const doRecommend = useCallback(async () => {
    bumpTelemetry(TEL_KEYS.search);
    setLoading(true);
    try {
      const tel = readTelemetry(startRef.current);
      const payload = autoMode
        ? {
            diet_type: diet,
            lat: geo.coords?.lat,
            lon: geo.coords?.lon,
            client_local_hour: new Date().getHours(),
            refresh_count: tel.refresh_count,
            search_count: tel.search_count,
            session_minutes: tel.session_minutes,
            auto_infer: true,
          }
        : {
            mood, weather, meal_type: mealType,
            spice_preference: spice, diet_type: diet,
            auto_infer: false,
          };
      const { data } = await api.post("/recommend", payload);
      setResults(data);
      if (data.context) setPreviewContext(data.context);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [autoMode, mood, weather, mealType, spice, diet, geo.coords]);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    doRecommend();
  }, [doRecommend]);

  const refresh = useCallback(async () => {
    bumpTelemetry(TEL_KEYS.refresh);
    if (autoMode) await doInferContext(geo.coords);
    await doRecommend();
  }, [autoMode, geo.coords, doInferContext, doRecommend]);

  const handleFeedback = useCallback(async (foodId, action) => {
    try {
      const ctx = results?.request || {};
      await api.post("/recommend/feedback", {
        food_id: foodId, action,
        context: { mood: ctx.mood, weather: ctx.weather, meal_type: ctx.meal_type },
      });
      toast.success(action === "like" ? "Saved to your taste DNA" : "Got it — we'll learn");
    } catch (e) {
      console.warn("[recommend/feedback] failed", e?.message);
      toast.error("Couldn't save feedback");
    }
  }, [results]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="recommend-page">
      <div className="overline mb-3">Mother Drive · Context Intelligence</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight max-w-2xl leading-tight">
        The less you tell us, the smarter we get.
      </h1>
      <p className="text-muted-foreground mt-3 max-w-xl">
        YUMO senses your time, weather and rhythm — so you don&apos;t have to spell it out.
      </p>

      <Card className="p-7 lg:p-10 rounded-2xl border-border shadow-none mt-10">
        {/* Auto-Sense toggle */}
        <div className="flex items-start justify-between gap-4 pb-6 mb-6 border-b border-border">
          <div className="flex gap-3">
            <div className={`mt-1 w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${autoMode ? "bg-primary/15 text-primary" : "bg-foreground/[0.04] text-muted-foreground"}`}>
              <Wand2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-medium" data-testid="auto-sense-title">Auto-Sense Mode</div>
              <div className="text-sm text-muted-foreground max-w-md">
                YUMO infers your mood, weather and meal from context — location, time and behaviour.
              </div>
            </div>
          </div>
          <Switch checked={autoMode} onCheckedChange={setAutoMode} data-testid="auto-sense-toggle" />
        </div>

        {/* What YUMO sees — only in auto mode */}
        {autoMode && (
          <div className="mb-7 p-5 rounded-xl bg-primary/[0.05] border border-primary/20" data-testid="context-preview">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-primary flex items-center gap-2">
                <Compass className="w-3.5 h-3.5" strokeWidth={1.75} /> What YUMO sees right now
              </div>
              {previewContext?.confidence != null && (
                <span className="text-xs text-muted-foreground">Confidence {previewContext.confidence}%</span>
              )}
            </div>

            {!previewContext && previewing && (
              <div className="text-sm text-muted-foreground">Reading your context…</div>
            )}

            {previewContext && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <Tile icon={<Clock className="w-3.5 h-3.5" strokeWidth={1.75} />} label="Time" value={previewContext.time_of_day} />
                  <Tile icon={<CloudSun className="w-3.5 h-3.5" strokeWidth={1.75} />} label="Weather" value={
                    previewContext.weather_meta?.temperature_c != null
                      ? `${previewContext.weather} · ${Math.round(previewContext.weather_meta.temperature_c)}°C`
                      : previewContext.weather
                  } />
                  <Tile icon={<MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />} label="Meal" value={previewContext.meal_type} />
                  <Tile icon={<Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />} label="Mood read" value={previewContext.inferred_mood} />
                </div>

                {previewContext.signals?.length > 0 && (
                  <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-muted-foreground" data-testid="context-signals">
                    {previewContext.signals.slice(0, 6).map((s, i) => (
                      <li key={`${s}-${i}`} className="flex gap-1.5"><span className="text-primary">·</span>{s}</li>
                    ))}
                  </ul>
                )}

                {geo.status === "denied" && (
                  <div className="mt-4 flex items-start gap-2 text-xs text-amber-700/90 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2" data-testid="geo-warning">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <span>Location permission denied — turn it on for true weather-aware picks, or switch to Manual mode.</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Manual form — only when auto mode is OFF */}
        <form onSubmit={onSubmit} className="space-y-8">
          {!autoMode && (
            <>
              <FieldGroup label="Current Mood">
                {MOODS.map((m) => <Chip key={m} testId={`mood-${m}`} active={mood === m} onClick={() => setMood(m)}>{m}</Chip>)}
              </FieldGroup>
              <FieldGroup label="Weather">
                {WEATHERS.map((w) => <Chip key={w} testId={`weather-${w}`} active={weather === w} onClick={() => setWeather(w)}>{w}</Chip>)}
              </FieldGroup>
              <FieldGroup label="Meal Type">
                {MEALS.map((m) => <Chip key={m} testId={`meal-${m}`} active={mealType === m} onClick={() => setMealType(m)}>{m}</Chip>)}
              </FieldGroup>
              <div className="grid md:grid-cols-2 gap-8">
                <FieldGroup label="Spice">
                  {SPICES.map((s) => <Chip key={s} testId={`spice-${s}`} active={spice === s} onClick={() => setSpice(s)}>{s}</Chip>)}
                </FieldGroup>
                <FieldGroup label="Diet">
                  {DIETS.map((d) => <Chip key={d} testId={`diet-${d}`} active={diet === d} onClick={() => setDiet(d)}>{d}</Chip>)}
                </FieldGroup>
              </div>
            </>
          )}

          {autoMode && (
            <FieldGroup label="Diet (the one thing we'll ask)">
              {DIETS.map((d) => <Chip key={d} testId={`diet-${d}`} active={diet === d} onClick={() => setDiet(d)}>{d}</Chip>)}
            </FieldGroup>
          )}

          <div className="flex flex-wrap gap-3">
            <Button type="submit" size="lg" className="rounded-full px-8" disabled={loading} data-testid="recommend-submit">
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.75} />
              {loading ? "Mother Drive is thinking…" : autoMode ? "Recommend for me now" : "Show me 25 picks"}
            </Button>
            {results && (
              <Button type="button" variant="outline" size="lg" className="rounded-full" onClick={refresh} disabled={loading} data-testid="recommend-refresh">
                <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.75} /> Refresh
              </Button>
            )}
          </div>
        </form>
      </Card>

      {results && (
        <div className="mt-12" data-testid="recommend-results">
          <div className="overline mb-3">Mother Drive&apos;s picks</div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight mb-8">
            For a {results.request.mood}, {results.request.weather} {results.request.meal_type}.
          </h2>

          {results.groups?.top_matches?.length > 0 && (
            <section className="mb-12" data-testid="rec-section-top">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-serif text-2xl font-semibold tracking-tight">Top Matches</h3>
                <span className="overline text-muted-foreground">Best fit for you right now</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {results.groups.top_matches.map((r, i) => (
                  <RecommendationCard key={r.food.id} rec={r} rank={i + 1} onFeedback={handleFeedback} />
                ))}
              </div>
            </section>
          )}

          {results.groups?.highly_relevant?.length > 0 && (
            <section className="mb-12" data-testid="rec-section-highly">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-serif text-2xl font-semibold tracking-tight">Highly Relevant</h3>
                <span className="overline text-muted-foreground">Strong matches you might like</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {results.groups.highly_relevant.map((r, i) => (
                  <RecommendationCard key={r.food.id} rec={r} rank={results.groups.top_matches.length + i + 1} onFeedback={handleFeedback} />
                ))}
              </div>
            </section>
          )}

          {results.groups?.explore_more?.length > 0 && (
            <section className="mb-6" data-testid="rec-section-explore">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-serif text-2xl font-semibold tracking-tight">Explore More</h3>
                <span className="overline text-muted-foreground">Stretch your taste a little</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {results.groups.explore_more.map((r, i) => (
                  <RecommendationCard
                    key={r.food.id}
                    rec={r}
                    rank={(results.groups.top_matches?.length || 0) + (results.groups.highly_relevant?.length || 0) + i + 1}
                    onFeedback={handleFeedback}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Fallback: legacy flat list (servers without grouped response) */}
          {!results.groups && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {results.recommendations.map((r, i) => (
                <RecommendationCard key={r.food.id} rec={r} rank={i + 1} onFeedback={handleFeedback} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <Label className="overline mb-3 block">{label}</Label>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Tile({ icon, label, value }) {
  return (
    <div className="bg-background/60 rounded-lg px-3 py-2 border border-border/60">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {icon}{label}
      </div>
      <div className="text-sm font-medium capitalize mt-0.5">{value || "—"}</div>
    </div>
  );
}
